import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Users, CheckCircle, XCircle, CreditCard,
  Loader2, AlertCircle, ArrowRight, Shield, Receipt
} from 'lucide-react';
import { bookingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) { resolve(true); return; }
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const fmt = (n) => n?.toLocaleString('en-IN') || '0';
const today = () => new Date().toISOString().split('T')[0];
const minCheckout = (checkIn) => {
  if (!checkIn) return today();
  const d = new Date(checkIn); d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const BookingWidget = ({ property }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const experiences = property?.experiences || ['stay'];
  const [experience, setExperience] = useState(experiences[0] || 'stay');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [guests, setGuests] = useState(2);
  const [step, setStep] = useState('form'); // form | checking | available | unavailable | paying | confirming | success
  const [pricing, setPricing] = useState(null);
  const [unavailReason, setUnavailReason] = useState('');
  const [error, setError] = useState('');
  const [bookingId, setBookingId] = useState('');

  const canCheck = experience === 'stay'
    ? (checkIn && checkOut && checkIn < checkOut)
    : !!eventDate;

  const handleCheckAvailability = async () => {
    if (!user) { navigate('/auth?return=true'); return; }
    setStep('checking'); setError('');
    try {
      const params = { experience, guests };
      if (experience === 'stay') { params.check_in = checkIn; params.check_out = checkOut; }
      else { params.event_date = eventDate; }
      const data = await bookingsAPI.checkAvailability(property.id, params);
      if (data.available) { setPricing(data); setStep('available'); }
      else { setUnavailReason(data.reason || 'Not available'); setStep('unavailable'); }
    } catch (e) { setError(e.message); setStep('form'); }
  };

  const handleBookNow = useCallback(async () => {
    if (!user) { navigate('/auth?return=true'); return; }
    setStep('paying'); setError('');
    const loaded = await loadRazorpay();
    if (!loaded) { setError('Could not load payment gateway'); setStep('available'); return; }

    try {
      const bookData = {
        property_id: property.id,
        experience_type: experience,
        check_in: experience === 'stay' ? checkIn : null,
        check_out: experience === 'stay' ? checkOut : null,
        event_date: experience === 'celebrate' ? eventDate : null,
        event_time: experience === 'celebrate' ? eventTime : null,
        guests, customer_name: user.name, customer_email: user.email, customer_phone: user.phone || '9999999999'
      };
      const booking = await bookingsAPI.initiate(bookData);

      new window.Razorpay({
        key: booking.razorpay_key_id,
        amount: booking.total * 100,
        currency: 'INR',
        order_id: booking.razorpay_order_id,
        name: 'HYGAD',
        description: `Booking: ${property.name}`,
        image: 'https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg',
        prefill: { name: user.name, email: user.email, contact: user.phone || '' },
        theme: { color: '#0A1628' },
        handler: async (response) => {
          setStep('confirming');
          try {
            const confirmed = await bookingsAPI.confirm({
              booking_id: booking.booking_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setBookingId(confirmed.booking_id);
            setStep('success');
          } catch (e) { setError(e.message); setStep('available'); }
        },
        modal: { ondismiss: () => { if (step === 'paying') setStep('available'); } }
      }).open();
    } catch (e) {
      if (e.message?.includes('409') || e.message?.toLowerCase().includes('available')) {
        setUnavailReason('These dates were just booked. Please try different dates.');
        setStep('unavailable');
      } else {
        setError(e.message); setStep('available');
      }
    }
  }, [user, property, experience, checkIn, checkOut, eventDate, eventTime, guests]);

  const reset = () => { setStep('form'); setPricing(null); setError(''); setUnavailReason(''); };

  return (
    <div data-testid="booking-widget" className="bg-white border border-gray-100 rounded-2xl overflow-hidden"
      style={{ boxShadow: '0 4px 24px rgba(10,22,40,0.08)' }}>
      {/* Header */}
      <div className="bg-navy px-6 py-4">
        <p className="text-gold text-xs uppercase tracking-[0.2em] font-medium">Instant Booking</p>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="font-serif text-3xl font-black text-white">₹{fmt(property?.price_min)}</span>
          <span className="text-white/50 text-sm">{experience === 'stay' ? '/ night' : '/ event'}</span>
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {/* FORM */}
          {step === 'form' && (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {experiences.length > 1 && (
                <div className="flex bg-gray-100 rounded-xl p-1 mb-5">
                  {experiences.map(exp => (
                    <button key={exp} type="button" onClick={() => { setExperience(exp); reset(); }}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${experience === exp ? 'bg-navy text-white shadow-sm' : 'text-gray-500'}`}>
                      {exp}
                    </button>
                  ))}
                </div>
              )}

              {experience === 'stay' ? (
                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">Check-in *</label>
                      <input data-testid="booking-checkin" type="date" value={checkIn} min={today()}
                        onChange={e => { setCheckIn(e.target.value); if (checkOut && e.target.value >= checkOut) setCheckOut(''); }}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">Check-out *</label>
                      <input data-testid="booking-checkout" type="date" value={checkOut} min={minCheckout(checkIn)}
                        onChange={e => setCheckOut(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Event Date *</label>
                    <input data-testid="booking-eventdate" type="date" value={eventDate} min={today()}
                      onChange={e => setEventDate(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Event Time</label>
                    <input data-testid="booking-eventtime" type="time" value={eventTime}
                      onChange={e => setEventTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold" />
                  </div>
                </div>
              )}

              <div className="mb-5">
                <label className="text-xs text-gray-500 font-medium block mb-1">Guests</label>
                <select data-testid="booking-guests" value={guests} onChange={e => setGuests(parseInt(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-navy outline-none focus:border-gold">
                  {Array.from({length: Math.min(property?.max_guests || 50, 50)}, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              {error && <p className="text-red-500 text-xs mb-3 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              <button data-testid="check-availability-btn" onClick={handleCheckAvailability} disabled={!canCheck}
                className="w-full bg-navy hover:bg-navy-light text-white py-3.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                <Calendar size={15} /> Check Availability
              </button>
              {!user && <p className="text-xs text-center text-gray-400 mt-2">You'll need to sign in to complete the booking</p>}
            </motion.div>
          )}

          {/* CHECKING */}
          {step === 'checking' && (
            <motion.div key="checking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
              <Loader2 size={32} className="text-gold animate-spin mx-auto mb-3" />
              <p className="text-sm font-medium text-navy">Checking availability...</p>
            </motion.div>
          )}

          {/* UNAVAILABLE */}
          {step === 'unavailable' && (
            <motion.div key="unavail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
              <XCircle size={36} className="text-red-400 mx-auto mb-3" />
              <p className="font-semibold text-navy mb-1">Not Available</p>
              <p className="text-sm text-gray-400 mb-5">{unavailReason}</p>
              <button onClick={reset} className="text-sm text-gold hover:text-gold-dark font-medium">Try Different Dates</button>
            </motion.div>
          )}

          {/* AVAILABLE — show pricing */}
          {(step === 'available' || step === 'paying' || step === 'confirming') && pricing && (
            <motion.div key="pricing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {/* Dates summary */}
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2 mb-5">
                <CheckCircle size={16} className="text-green-500 shrink-0" />
                <div className="text-sm text-green-800">
                  <span className="font-semibold">Available!</span>
                  {experience === 'stay'
                    ? <> {checkIn} → {checkOut} · {pricing.nights} night{pricing.nights > 1 ? 's' : ''}</>
                    : <> {eventDate}{eventTime ? ` at ${eventTime}` : ''}</>
                  }
                  · {guests} guest{guests > 1 ? 's' : ''}
                </div>
              </div>

              {/* Price breakdown */}
              <div className="bg-[#FAFAF9] rounded-xl p-4 mb-5 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>₹{fmt(pricing.price_per_night)} × {pricing.nights} {experience === 'stay' ? `night${pricing.nights > 1 ? 's' : ''}` : 'day'}</span>
                  <span>₹{fmt(pricing.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>GST ({pricing.tax_rate}%)</span>
                  <span>₹{fmt(pricing.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Convenience fee ({pricing.convenience_fee_rate}%)</span>
                  <span>₹{fmt(pricing.convenience_fee)}</span>
                </div>
                <div className="flex justify-between font-bold text-navy pt-2 border-t border-gray-200 text-base">
                  <span>Total</span>
                  <span>₹{fmt(pricing.total)}</span>
                </div>
              </div>

              {error && <p className="text-red-500 text-xs mb-3 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              <button data-testid="book-now-btn" onClick={handleBookNow}
                disabled={step === 'paying' || step === 'confirming'}
                className="w-full bg-gold hover:bg-gold-dark text-white py-4 rounded-xl text-sm font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {step === 'paying' || step === 'confirming'
                  ? <><Loader2 size={16} className="animate-spin" />{step === 'confirming' ? 'Confirming...' : 'Opening Checkout...'}</>
                  : <><CreditCard size={16} /> Pay ₹{fmt(pricing.total)} — Book Now</>
                }
              </button>
              <button onClick={reset} className="w-full text-xs text-gray-400 hover:text-gray-600 mt-2 py-1.5">Change dates</button>

              <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-400">
                <Shield size={11} /> Secured by Razorpay · UPI, Cards, Net Banking
              </div>
            </motion.div>
          )}

          {/* SUCCESS */}
          {step === 'success' && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h3 className="font-serif text-xl font-bold text-navy mb-1">Booking Confirmed!</h3>
              <p className="text-gray-500 text-sm mb-1">Booking ID:</p>
              <p data-testid="booking-id-display" className="font-mono text-gold font-bold text-lg mb-4">{bookingId}</p>
              <p className="text-xs text-gray-400 mb-5">Confirmation email sent to {user?.email}</p>
              <div className="flex flex-col gap-2">
                <button onClick={() => navigate(`/booking/${bookingId}`)}
                  className="bg-navy text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:bg-navy-light transition-colors">
                  <Receipt size={14} /> View Booking & Receipt
                </button>
                <button onClick={() => navigate('/customer')}
                  className="text-sm text-gold font-medium py-2 hover:underline">
                  My Bookings Dashboard
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingWidget;
